// src/pages/Users/Messaging/CreateMessageForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import Select, { components } from "react-select";
import { toast } from "react-toastify";
import { sendMessage } from "../../../services/messageServices";
import { getAllUsers } from "../../../services/userServices";
import { IoIosSend } from "react-icons/io";
import { FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";

/* Quill */
const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};
const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "clean",
];

/* Sujet */
const SUBJECT_OPTIONS = [
  "Adhésion",
  "Dossier d'inscription",
  "Règlement",
  "Informations cours",
  "Absence",
  "Inscription compétition",
  "Perte d'objet",
  "Autre (saisir)",
];

/* Rôle → badge */
function roleMeta(role_id) {
  if (role_id === 1) return { label: "Administrateur", tone: "red" };
  if (role_id === 3) return { label: "Enseignant", tone: "green" };
  if (role_id === 2) return { label: "Utilisateur", tone: "slate" };
  return { label: "", tone: "slate" };
}
function roleBadgeClass(tone) {
  switch (tone) {
    case "red":
      return "bg-red-600/90 text-white";
    case "green":
      return "bg-emerald-600/90 text-white";
    default:
      return "bg-slate-600/80 text-white";
  }
}

/* Option custom */
const CustomOption = (props) => {
  const { data } = props;
  const badge = data.roleLabel ? (
    <span
      className={`ml-2 inline-flex items-center rounded-full px-2 py-[2px] text-[10px] font-semibold ${roleBadgeClass(
        data.roleTone
      )}`}
    >
      {data.roleLabel}
    </span>
  ) : null;

  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <img
          src={data.photo || "/img/user_avatar.jpg"}
          alt={data.label}
          className="w-6 h-6 rounded-full object-cover ring-1 ring-slate-300/60"
        />
        <div className="flex flex-col">
          <span className="text-sm">
            {data.name}
            {badge}
          </span>
          {data.secondary && (
            <span className="text-[11px] text-slate-300">{data.secondary}</span>
          )}
        </div>
      </div>
    </components.Option>
  );
};

/* Valeur sélectionnée custom */
const CustomSingleValue = (props) => {
  const { data } = props;
  const badge = data.roleLabel ? (
    <span
      className={`ml-2 inline-flex items-center rounded-full px-2 py-[2px] text-[10px] font-semibold ${roleBadgeClass(
        data.roleTone
      )}`}
    >
      {data.roleLabel}
    </span>
  ) : null;

  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <img
          src={data.photo || "/img/user_avatar.jpg"}
          alt={data.label}
          className="w-5 h-5 rounded-full object-cover ring-1 ring-slate-300/60"
        />
        <span className="text-sm">
          {data.name}
          {badge}
        </span>
      </div>
    </components.SingleValue>
  );
};

/* Normalisation (ignore accents / casse) */
const normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/* Filtre de recherche: nom/prénom + adhérents + rôle */
const makeFilterOption = () => (option, rawInput) => {
  const input = normalize(rawInput);
  if (!input) return true;
  const d = option.data || {};
  const hay = [
    d.label, // fullName
    d.name, // idem
    d.secondary, // "Adhérents: ..."
    d.roleLabel, // Administrateur / Enseignant
  ]
    .map(normalize)
    .join(" ");
  return hay.includes(input);
};

export default function CreateMessageForm({
  onSuccess,
  show,
  onHide,
  userId,
  userRole, // "user" | "admin" | "enseignant"
  initialRecipientId = "",
  initialSubject = "",
}) {
  const [messageData, setMessageData] = useState({
    recipient_id: initialRecipientId || "",
    subject: initialSubject || "",
    content: "",
  });
  const [recipients, setRecipients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const initialSubjectKnown = useMemo(
    () =>
      initialSubject &&
      SUBJECT_OPTIONS.some(
        (opt) => opt.toLowerCase() === initialSubject.toLowerCase()
      ),
    [initialSubject]
  );
  const [subjectChoice, setSubjectChoice] = useState(
    initialSubject
      ? initialSubjectKnown
        ? initialSubject
        : "Autre (saisir)"
      : ""
  );
  const [customSubject, setCustomSubject] = useState(
    initialSubject && !initialSubjectKnown ? initialSubject : ""
  );

  const isEnabled = (v) => v === true || v === 1 || v === "1";

  /* Fetch destinataires */
  useEffect(() => {
    if (!show) return;
    const fetchRecipients = async () => {
      setIsLoading(true);
      try {
        const list = await getAllUsers();
        let users = Array.isArray(list) ? list : [];

        // Un "user" (role_id 2) ne voit que les Admin/Enseignants qui acceptent les messages
        if (userRole === "user") {
          users = users.filter(
            (u) =>
              [1, 3].includes(Number(u.role_id)) &&
              isEnabled(u.receive_messages)
          );
        }

        setRecipients(users);
      } catch (error) {
        console.error("Erreur lors du chargement des destinataires:", error);
        toast.error("Erreur lors du chargement des destinataires.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchRecipients();
  }, [userRole, show]);

  /* Réinit à l’ouverture/changement des props */
  useEffect(() => {
    setMessageData({
      recipient_id: initialRecipientId || "",
      subject: initialSubject || "",
      content: "",
    });

    const known =
      initialSubject &&
      SUBJECT_OPTIONS.some(
        (opt) => opt.toLowerCase() === initialSubject.toLowerCase()
      );
    setSubjectChoice(
      initialSubject ? (known ? initialSubject : "Autre (saisir)") : ""
    );
    setCustomSubject(initialSubject && !known ? initialSubject : "");
  }, [initialRecipientId, initialSubject, show]);

  /* Options Select (avec avatar + badge rôle, et — pour admin/enseignant — liste d’adhérents recherchable) */
  const options = useMemo(() => {
    return (recipients || []).map((user) => {
      const photo = toAbsoluteUserPhotoUrl(
        user.photoURL || user.photo_url || "/img/user_avatar.jpg"
      );
      const fullName = `${user.nom} ${user.prenom}`;
      const { label: rLabel, tone: rTone } = roleMeta(user.role_id);

      if (userRole === "user") {
        return {
          value: user.id,
          label: fullName,
          name: fullName,
          photo,
          roleLabel: rLabel,
          roleTone: rTone,
          secondary: "", // pas d’adhérents visibles côté user
        };
      } else {
        const secondary = user.adherents?.length
          ? user.adherents.map((a) => `${a.prenom.trim()} ${a.nom}`).join(", ")
          : "";
        return {
          value: user.id,
          label: fullName,
          name: fullName,
          photo,
          roleLabel: rLabel,
          roleTone: rTone,
          secondary: secondary ? `Adhérents: ${secondary}` : "",
        };
      }
    });
  }, [recipients, userRole]);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === messageData.recipient_id) || null,
    [options, messageData.recipient_id]
  );

  const onRecipientChange = (opt) => {
    setMessageData((p) => ({ ...p, recipient_id: opt?.value || "" }));
  };

  /* Sujet */
  const handleSubjectChoice = (e) => {
    const val = e.target.value;
    setSubjectChoice(val);
    if (val === "Autre (saisir)") {
      setMessageData((p) => ({ ...p, subject: customSubject || "" }));
    } else {
      setMessageData((p) => ({ ...p, subject: val }));
    }
  };
  const handleCustomSubject = (e) => {
    const val = e.target.value;
    setCustomSubject(val);
    if (subjectChoice === "Autre (saisir)") {
      setMessageData((p) => ({ ...p, subject: val }));
    }
  };

  /* Contenu */
  const handleContentChange = (content) => {
    setMessageData((p) => ({ ...p, content }));
  };

  const canSubmit =
    !!messageData.recipient_id &&
    !!(messageData.subject || customSubject) &&
    !!messageData.content;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }
    try {
      setSending(true);
      await sendMessage({
        ...messageData,
        sender_id: userId,
        subject:
          subjectChoice === "Autre (saisir)"
            ? (customSubject || "").trim()
            : (messageData.subject || "").trim(),
      });
      toast.success("Message envoyé avec succès.");
      onSuccess?.();
      onHide?.();
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error);
      toast.error("Erreur lors de l'envoi du message.");
    } finally {
      setSending(false);
    }
  };

  if (!show) return null;

  return (
    <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/70 backdrop-blur">
      <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5">
        {/* Destinataire avec recherche nom/prénom & adhérents (admin/enseignant) */}
        <div>
          <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
            Destinataire
          </label>
          {isLoading ? (
            <div className="h-10 w-full rounded-xl bg-slate-200 dark:bg-slate-800 animate-pulse" />
          ) : (
            <Select
              classNamePrefix="rs"
              className="rs-container"
              name="recipient_id"
              options={options}
              value={selectedOption}
              onChange={onRecipientChange}
              placeholder="Sélectionnez un destinataire…"
              isClearable
              components={{
                Option: CustomOption,
                SingleValue: CustomSingleValue,
              }}
              filterOption={makeFilterOption()}
              noOptionsMessage={() => "Aucun résultat"}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderRadius: 12,
                  backgroundColor: "transparent",
                  borderColor: state.isFocused
                    ? "#7c3aed"
                    : "rgba(148,163,184,.4)",
                  boxShadow: "none",
                  minHeight: 42,
                  ":hover": { borderColor: "#7c3aed" },
                  color: "inherit",
                }),
                menu: (base) => ({
                  ...base,
                  borderRadius: 12,
                  overflow: "hidden",
                  backgroundColor: "rgb(15 23 42 / .95)",
                  color: "white",
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused
                    ? "rgba(124,58,237,.2)"
                    : "transparent",
                  color: "white",
                  ":active": { backgroundColor: "rgba(124,58,237,.35)" },
                }),
                singleValue: (base) => ({ ...base, color: "inherit" }),
                placeholder: (base) => ({ ...base, color: "rgb(148 163 184)" }),
                input: (base) => ({ ...base, color: "inherit" }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              menuPortalTarget={document.body}
            />
          )}
        </div>

        {/* Sujet */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-3">
          <div>
            <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
              Objet
            </label>
            <select
              value={subjectChoice}
              onChange={handleSubjectChoice}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            >
              <option value="">Sélectionnez une option</option>
              {SUBJECT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {subjectChoice === "Autre (saisir)" && (
            <div>
              <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
                Saisissez votre objet
              </label>
              <input
                type="text"
                value={customSubject}
                onChange={
                  setCustomSubject && ((e) => setCustomSubject(e.target.value))
                }
                placeholder="Ex. : Question sur la tenue, demande d’info…"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          )}
        </div>

        {/* Contenu */}
        <div>
          <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
            Contenu du message
          </label>
          <div className="rounded-xl overflow-hidden ring-1 ring-slate-300 dark:ring-slate-700">
            <ReactQuill
              theme="snow"
              value={messageData.content}
              onChange={(val) =>
                setMessageData((p) => ({ ...p, content: val }))
              }
              modules={quillModules}
              formats={quillFormats}
              placeholder="Rédigez votre message ici…"
              className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onHide}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <FaTimes className="text-[12px]" />
            Annuler
          </button>
          <Button
            type="submit"
            text={sending ? "Envoi…" : "Envoyer"}
            icon={IoIosSend}
            className="gap-2 !rounded-lg"
            disabled={
              !messageData.recipient_id ||
              !(messageData.subject || customSubject) ||
              !messageData.content ||
              sending ||
              isLoading
            }
          />
        </div>
      </form>
    </div>
  );
}
